import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { checkLogsDirExists, D3_BASE_PATH, getPlatformConfig } from '../file/constants';
import JSZip from 'jszip';
import { Capacitor } from '@capacitor/core';
import { SourceMapConsumer } from 'source-map-js';

// import { getLogLine } from 'd3components/src/utils/tools';

// 日志级别类型
export type LogLevel = 'info' | 'warn' | 'error' | 'log';

// 全局 info 日志开关：根据环境自动设置
let INFO_LOGGING_ENABLED = false;

// 自动根据环境设置 info 日志开关
function initInfoLoggingEnabled() {
  // Web 开发环境：自动开启 info 日志记录
  if (import.meta.env.DEV && Capacitor.getPlatform() === 'web') {
    INFO_LOGGING_ENABLED = true;
  }
}

// 修改全局变量，打开/关闭 info 日志记录
export function setInfoLoggingEnabled(enabled: boolean): void { INFO_LOGGING_ENABLED = enabled; }
// 获取全局 info 日志开关状态
export function isInfoLoggingEnabled(): boolean { return INFO_LOGGING_ENABLED; }

// 判断当前平台是否支持文件系统操作
// Web 平台的文件系统 API 受限，通常不支持文件写入
function shouldPerformFileOperations(): boolean {
  const platform = Capacitor.getPlatform();
  // iOS 和 Android 平台支持完整的文件系统操作
  if (platform === 'ios' || platform === 'android') {
    return true;
  }
  // Web 平台默认不支持文件系统操作
  // 其他平台（如果有）也默认不支持，除非明确支持
  return false;
}

// 日志（周）文件名表
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
class FileIO {
  // 标记目录是否已经准备就绪
  private logsDirReady = false;
  // 获取平台配置（缓存，避免重复调用）
  private getDirectory(): Directory {
    return getPlatformConfig().directory;
  }
  //
  // 检查日志目录是否存在（目录已在权限检查时创建）
  //
  async ensureDirs(): Promise<void> {
    if (this.logsDirReady) return;
    const logsDirExists = await checkLogsDirExists();
    if (!logsDirExists) {
      log.error('日志目录不存在，请检查权限和目录创建流程');
      throw new Error('日志目录不存在，请检查权限和目录创建流程');
    }
    this.logsDirReady = true;
    log.log(' Logger: 日志目录已存在');
  }
  //
  // 清空文件内容
  //
  async truncate(path: string): Promise<void> {
    await Filesystem.writeFile({ path, data: '', directory: this.getDirectory(), encoding: Encoding.UTF8 });
  }
  //
  // 文件分割并保存为zip
  //当日志文件达到最大行数时，将当前文件内容压缩为zip并清空原文件
  //
  async rotate(dayOfWeek: number, path: string, when: Date): Promise<void> {
    log.warn('文件分割并保存为zip');
    const directory = this.getDirectory();
    const name = DAY_NAMES[dayOfWeek] || 'unknown';
    const pad = (n: number) => n < 10 ? `0${n}` : String(n);
    const dayKey = `${when.getFullYear()}${pad(when.getMonth() + 1)}${pad(when.getDate())}`; // 按天聚合
    const timePart = `${pad(when.getHours())}${pad(when.getMinutes())}${pad(when.getSeconds())}`; // 条目名中的时分秒
    // 压缩包与内部文件名（仅允许安全字符）
    const sanitize = (str: string) => str.replace(/[^a-zA-Z0-9_\-\.]/g, '_');
    const zipPath = `${D3_BASE_PATH}/logs/${sanitize(`${name}-${dayKey}.zip`)}`;
    const innerFileName = sanitize(`${name}-${dayKey}-${timePart}.txt`);
    try {
      // 读取被分割的日志内容
      const { data: logContent } = await Filesystem.readFile({ path: path, directory, encoding: Encoding.UTF8 });
      if (!logContent) {
        await Filesystem.writeFile({ path, data: '', directory, encoding: Encoding.UTF8 });
        return;
      }
      const zip = new JSZip();
      // 如果当天 zip 已存在则加载后再追加
      try {
        const exist = await Filesystem.readFile({ path: zipPath, directory });
        if (exist && exist.data) {
          // 如果当天 zip 已存在则加载后再追加
          await zip.loadAsync(String(exist.data), { base64: true });
        }
      } catch (_) {
        // 不存在则创建新 zip 
      }
      // 追加一个新的条目
      zip.file(innerFileName, logContent);
      const payload = await zip.generateAsync({
        type: 'base64',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      await Filesystem.writeFile({ path: zipPath, data: payload as unknown as string | Blob, directory });
      const stats = await Filesystem.stat({ path: zipPath, directory });
      if (stats.size === 0) throw new Error('ZIP文件写入为空');
      // 清空原始当天文件，继续新的写入周期
      await Filesystem.writeFile({ path, data: '', directory, encoding: Encoding.UTF8 });
      return;
    } catch (error) {
      log.error('创建或追加 zip 失败:', error);
    }
    // 兜底：将当前内容直接归档为单独的 txt
    try {
      await Filesystem.rename({ from: path, to: `${D3_BASE_PATH}/logs/${name}-${dayKey}-${timePart}.txt`, directory });
      await Filesystem.writeFile({ path, data: '', directory, encoding: Encoding.UTF8 });
    } catch (_) {
      await Filesystem.writeFile({ path: `${D3_BASE_PATH}/logs/${name}-${dayKey}-${timePart}.txt`, data: '', directory, encoding: Encoding.UTF8 });
      await Filesystem.writeFile({ path, data: '', directory, encoding: Encoding.UTF8 });
    }
  }

  //
  // 追加内容到文件
  //
  async append(path: string, content: string): Promise<void> {
    await Filesystem.appendFile({ path, data: content, directory: this.getDirectory(), encoding: Encoding.UTF8 });
  }
  //
  // 删除跨周的所有打包文件
  //
  async deleteArchiveFiles(dayOfWeek: number, currentDate: string): Promise<void> {
    try {
      const directory = this.getDirectory();
      const name = DAY_NAMES[dayOfWeek] || 'unknown';
      // 获取日志目录所有文件
      const dir = await Filesystem.readdir({ path: `${D3_BASE_PATH}/logs`, directory });
      // 提取文件名，过滤掉空值，过滤掉非字符串值，x 是字符串，直接返回，x 是对象，返回 x.name 属性
      const names = (dir.files || []).map((x: any) => typeof x === 'string' ? x : x?.name).filter(Boolean);
      // 过滤出匹配的zip文件
      const targets = names.filter(n => n.startsWith(`${name}-`) && n.endsWith('.zip') && !n.includes(currentDate));
      // 如果匹配的zip文件大于0，则删除匹配的zip文件
      if (targets.length > 0) {
        await Promise.allSettled(targets.map(n => Filesystem.deleteFile({ path: `${D3_BASE_PATH}/logs/${n}`, directory })));
        log.warn('已删除跨周zip文件', targets);
      }
    } catch (e) {
      log.error('删除zip文件失败:', e);
    }
  }
}

//
// 管理日志缓冲、批量处理
//
class BufferManager {
  public buffer: Array<{ level: LogLevel; message: string; ts: Date }> = [];
  private batchSize = 10;
  private lastWriteDateByWeekday: Record<number, string> = {};
  private lastCleanDateByWeekday: Record<number, string> = {};
  private flushing = false;
  // 标记是否正在 flush，用于防止 flush 期间的日志写入文件
  public isFlushing = false;
  //
  // 当前星期文件的最大行数
  //
  private maxLinesPerFile = 100;
  // 当前星期文件的行数（精确方案，已改为基于 flush 次数，保留以便需要时恢复）
  // private lineCountByWeekday: Record<number, number> = {};
  // 基于 flush 次数的近似方案：每次 flush 记为写入 batchSize 行
  private flushCountByWeekday: Record<number, number> = {};
  // 存储历史日志用于查看器显示
  // 注意：historyLogs 是“运行时内存缓存”。
  // - 它并不是浏览器 DevTools 的 console 历史（浏览器没有 API 允许回读 DevTools 的历史输出）。
  // - 只有在 hookConsole() 生效之后产生的 console 输出，才会被采集进来。
  private historyLogs: Array<{ level: LogLevel; message: string; ts: Date }> = [];
  // 历史日志最大保存数量
  private maxHistorySize = 1000;
  constructor(private fileIO: FileIO) { }
  //
  // 开始记录日志到内存缓冲区
  //
  enqueue(level: LogLevel, message: string) {
    const logTs = new Date();
    const logEntry = { level, message, ts: logTs };

    // 统一检查是否应该记录这个级别的日志
    // - error / warn / log 永远进入内存历史（LogViewer 依赖 historyLogs 展示）
    // - info 是否记录由 INFO_LOGGING_ENABLED 控制
    // 只影响“是否写入 logger 的 historyLogs / 文件”，不影响浏览器/原生控制台本身是否输出。
    const shouldRecord = level === 'error' || level === 'warn' || level === 'log' || isInfoLoggingEnabled();

    if (!shouldRecord) {
      // 不记录info级别日志（当开关关闭时）
      return;
    }
    // 添加到历史日志
    this.addToHistory(logEntry);
    // 添加到buffer（用于文件写入）
    this.buffer.push(logEntry);
    // 文件写入逻辑
    // 只在支持的平台（iOS/Android）执行文件写入
    if (this.buffer.length >= this.batchSize && shouldPerformFileOperations()) {
      const dayName = DAY_NAMES[logTs.getDay()];
      const fileName = `${D3_BASE_PATH}/logs/${dayName}.txt`;
      void this.flush(logTs, fileName, logTs.getDay());
    }
  }

  //
  // 批量刷新缓冲区到文件（安全的权限检查版本）
  //
  async flush(logTs: Date, filePath: string, weekday: number): Promise<void> {
    // 判断是否正在刷新，或者缓冲区为空，直接返回
    if (this.flushing || this.buffer.length === 0) return;
    this.flushing = true;
    this.isFlushing = true;
    try {
      // 避免期间新增的日志被误清或混入
      const batch = this.buffer.splice(0, this.buffer.length);

      await this.fileIO.ensureDirs();
      // 格式化日志行格式
      const pad = (n: number) => n < 10 ? `0${n}` : String(n);
      const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      const lines = batch.map(({ level, message, ts }) => `[${fmt(ts)}][${level}] ${message ?? ''}`);
      // 检查是否需要清空文件（跨周）
      const lastClean = this.lastCleanDateByWeekday[weekday];
      const currentDate = `${logTs.getFullYear()}-${pad(logTs.getMonth() + 1)}-${pad(logTs.getDate())}`;
      log.warn('跨周检查:', { weekday, lastClean, currentDate });
      // 如果lastClean不为空，则表示已经清理过，需要检查是否需要再次清理
      if (lastClean) {
        // 计算时间差，如果时间差大于等于7天，则清空文件，并删除对应的打包文件
        const p = lastClean.split('-');
        const diffDays = Math.abs(
          +new Date(logTs.getFullYear(), logTs.getMonth(), logTs.getDate()) -
          +new Date(+p[0], +p[1] - 1, +p[2])
        ) / (1000 * 60 * 60 * 24);
        log.warn('时间差计算:', { lastClean, diffDays, needTruncate: diffDays >= 7 });
        if (p.length === 3 && diffDays >= 7) {
          log.warn('执行跨周清空和删除');
          await this.fileIO.truncate(filePath);
          // this.lineCountByWeekday[weekday] = 0; // 精确方案
          this.flushCountByWeekday[weekday] = 0; // 近似方案
          // 跨周时同时删除对应的打包文件
          await this.fileIO.deleteArchiveFiles(weekday, currentDate);
          // 记录本次清理日期，避免重复清理
          this.lastCleanDateByWeekday[weekday] = currentDate;
        }
      } else {
        // 首次运行，检查现有文件是否需要清理（而不是直接清空）
        log.warn('首次跨周检查，检查现有文件');

        // 检查文件是否存在，如果存在则检查文件修改时间
        try {
          const directory = getPlatformConfig().directory;
          const fileStat = await Filesystem.stat({
            path: filePath,
            directory
          });

          if (fileStat && fileStat.mtime) {
            const fileDate = new Date(fileStat.mtime);
            const fileDateStr = `${fileDate.getFullYear()}-${pad(fileDate.getMonth() + 1)}-${pad(fileDate.getDate())}`;

            // 计算文件创建时间与当前时间的差值
            const diffDays = Math.abs(
              +new Date(logTs.getFullYear(), logTs.getMonth(), logTs.getDate()) -
              +new Date(fileDate.getFullYear(), fileDate.getMonth(), fileDate.getDate())
            ) / (1000 * 60 * 60 * 24);
            log.warn('文件时间检查:', { fileDateStr, currentDate, diffDays });
            // 只有当文件超过7天时才清理
            if (diffDays >= 7) {
              log.warn('文件超过7天，执行清理');
              await this.fileIO.truncate(filePath);
              this.flushCountByWeekday[weekday] = 0;
              await this.fileIO.deleteArchiveFiles(weekday, currentDate);
            } else {
              log.warn('文件未超过7天，保留现有内容');
            }
          } else {
            log.warn('文件不存在或无法获取时间，跳过清理');
          }
        } catch (error) {
          log.warn('检查文件状态失败，跳过清理:', error);
        }
        // 记录本次检查日期
        this.lastCleanDateByWeekday[weekday] = currentDate;
      }
      // 基于 flush 次数的近似初始化
      if (this.flushCountByWeekday[weekday] === undefined) {
        this.flushCountByWeekday[weekday] = 0;
      }
      // 写入日志并更新计数
      await this.fileIO.append(filePath, lines.join('\n') + '\n');
      // 每次 flush 成功后将 flush 次数 +1（允许误差为不足一批的情况）
      this.flushCountByWeekday[weekday] = (this.flushCountByWeekday[weekday] || 0) + 1;
      const approxLines = this.flushCountByWeekday[weekday] * this.batchSize;
      log.warn('近似行数:', { approxLines, maxLinesPerFile: this.maxLinesPerFile });
      // 如果近似行数达到或超过阈值，则进行分割
      if (approxLines >= this.maxLinesPerFile) {
        log.warn('近似行数达到或超过阈值，进行分割');
        await this.fileIO.rotate(weekday, filePath, logTs);
        // 分割后按整批数量扣减一次文件容量对应的批次数
        const batchesPerFile = Math.floor(this.maxLinesPerFile / this.batchSize);
        this.flushCountByWeekday[weekday] = Math.max(0, this.flushCountByWeekday[weekday] - batchesPerFile);
      }
      // 更新最后写入日期
      this.lastWriteDateByWeekday[weekday] = `${logTs.getFullYear()}-${pad(logTs.getMonth() + 1)}-${pad(logTs.getDate())}`;
    } finally {
      // 结束
      this.flushing = false;
      this.isFlushing = false;
      // 如果缓冲区长度大于等于缓冲区最大值大小，继续刷新
      if (this.buffer.length >= this.batchSize) {
        // 获取缓冲区最后一个元素
        const latestLog = this.buffer[this.buffer.length - 1];
        // 将下一次刷新推迟到当前事件循环结束后，用最新日志的时间戳作为下次刷新的时间参数
        setTimeout(() => void this.flush(latestLog.ts, filePath, weekday), 0);
      }
    }
  }
  //
  // 强制刷新所有缓冲区的日志，不管缓冲区有多少条，都写入文件，并清空缓冲区
  //
  async flushAll(): Promise<void> {
    if (this.buffer.length === 0) return;

    // 只在支持的平台执行文件写入
    if (!shouldPerformFileOperations()) {
      const platform = Capacitor.getPlatform();
      log.warn(`平台 ${platform} 不支持文件系统操作，跳过 flushAll`);
      return;
    }

    // 这里确保把应用退出或者是切换时把日志写入到对应的天文件
    const logTs = new Date();
    const dayName = DAY_NAMES[logTs.getDay()];
    const fileName = `${D3_BASE_PATH}/logs/${dayName}.txt`;
    await this.flush(logTs, fileName, logTs.getDay());
    log.warn('应用退出时，强制刷新所有缓冲区的日志');
  }
  // 添加到历史日志
  private addToHistory(logEntry: { level: LogLevel; message: string; ts: Date }): void {
    // 添加新日志到历史记录末尾
    this.historyLogs.push(logEntry);
    // 超过最大数量限制移除最旧的日志
    if (this.historyLogs.length > this.maxHistorySize) this.historyLogs.shift();
  }
  //
  // 获取所有历史日志数据
  //返回历史日志的完整副本，供日志查看器使用，包含所有已记录的日志，包括已写入文件的日志
  //
  getAllLogs(): Array<{ level: LogLevel; message: string; ts: Date }> {
    // 返回数组副本，防止外部代码修改内部数据
    // 使用展开运算符创建浅拷贝，保护原始数据
    return [...this.historyLogs];
  }
  // 清除日志查看页面的历史日志
  clearLogs(): void { this.historyLogs = []; }
}
// 日志记录器
export class SimpleLogger {
  private static instance: SimpleLogger; // 
  private bufferManager: BufferManager; // 批量管理器实例
  // 获取单例实例
  public static getInstance(): SimpleLogger {
    if (!SimpleLogger.instance) {  //检查实例是否存在
      SimpleLogger.instance = new SimpleLogger();  //如果实例不存在，则创建实例
    }
    return SimpleLogger.instance;  //返回实例
  }
  private constructor() {
    const fileIO = new FileIO(); //创建文件IO实例
    this.bufferManager = new BufferManager(fileIO);  //创建缓冲区管理器实例
  }
  public info(message: string, data?: any): void {
    this.bufferManager.enqueue('info', data ? `${message}: ${JSON.stringify(data)}` : message);
  }
  //记录警告级别日志
  public warn(message: string, data?: any): void {
    this.bufferManager.enqueue('warn', data ? `${message}: ${JSON.stringify(data)}` : message);
  }
  // 记录错误级别日志
  public error(message: string, data?: any): void {
    this.bufferManager.enqueue('error', data ? `${message}: ${JSON.stringify(data)}` : message);
  }
  public log(message: string, data?: any): void {
    this.bufferManager.enqueue('log', data ? `${message}: ${JSON.stringify(data)}` : message);
  }
  // 强制刷新所有缓冲区的日志（应用退出时调用）
  public async flushAll(): Promise<void> {
    await this.bufferManager.flushAll();
  }
  // 获取所有历史日志数据返回完整的日志历史，包括已写入文件的日志返回所有历史日志（最多1000条）
  public getAllLogs(): Array<{ level: LogLevel; message: string; ts: Date }> {
    return this.bufferManager.getAllLogs();
  }
  // 清除日志查看页面的历史日志
  clearLogs(): void {
    this.bufferManager.clearLogs();
  }
  // 检查是否正在 flush（供内部日志使用）
  public isFlushing(): boolean {
    return this.bufferManager.isFlushing;
  }
}
// 导出单例实例，供全局使用
export const logger = SimpleLogger.getInstance();
// ----------------------------
// Console Hook
// ----------------------------
// ========================================
// Console 拦截核心实现
// ========================================

//
// 保存原生 console 方法的引用
// 用于避免递归调用，确保真正的控制台输出
//
let originalConsole: {
  log: typeof console.log;
  info: typeof console.info;
  warn: typeof console.warn;
  error: typeof console.error;
};

//
// 标记是否正在处理 logger 内部的日志
//用于防止无限递归调用
//
let isLoggerInternal = false;

//
// 统一的日志处理函数
// 负责：1. 原生控制台输出 2. 消息序列化 3. 文件写入
//
// @param args - 日志参数数组
// @param level - 日志级别
//
const sourceMapCache: Record<string, SourceMapConsumer | null> = {};

// Sourcemap 映射开关：
// 方便在真机/设备上直接看到源码位置。
const ENABLE_SOURCEMAP_MAPPING = true;

//
// 将压缩后的堆栈行映射回源码位置
//@param stackLine - 压缩后的堆栈行
//@returns 源码位置
//
async function mapToSource(stackLine: string): Promise<string> {
  if (!ENABLE_SOURCEMAP_MAPPING) return stackLine;
  // 匹配形如 /assets/js/index-ABC123.js:2:38372
  const match = stackLine.match(/(\/assets\/js\/[^:]+):(\d+):(\d+)/);
  if (!match) return stackLine; // 如果匹配失败，返回原始堆栈行

  const [, fileUrl, lineStr, columnStr] = match;
  // 获取 SourceMap，将压缩后位置映射回源码
  if (sourceMapCache[fileUrl] === undefined) {
    try {
      // 请求 SourceMap 文件并解析
      const res = await fetch(`${fileUrl}.map`);
      // if (!res.ok) throw new Error('Map not found');
      // const consumer = await new SourceMapConsumer(await res.json());
      // // 缓存结果
      // sourceMapCache[fileUrl] = consumer;
      // if (res.ok) {
      //   sourceMapCache[fileUrl] = await new SourceMapConsumer(await res.json());
      // } else {
      //   sourceMapCache[fileUrl] = null;
      // }
      sourceMapCache[fileUrl] = res.ok
        ? await new SourceMapConsumer(await res.json())
        : null;
    } catch { // 获取失败，缓存 null
      sourceMapCache[fileUrl] = null;
    }
  }
  const consumer = sourceMapCache[fileUrl];
  if (!consumer) return stackLine; // 获取失败，返回原样
  // 查找原始位置
  const pos = consumer.originalPositionFor({
    line: parseInt(lineStr),
    column: parseInt(columnStr)
  });

  if (pos.source && pos.line) {
    // 转换路径并返回原始位置
    return `${pos.source.replace(/^webpack:\/\/\/?src\//, 'src/')}:${pos.line}:${pos.column ?? 0}`;
  }

  return stackLine;
}



//跳过 logger 内部函数
const loggerInternalPatterns = [
  'processLogOutput', // 当前处理函数
  'intercept',   // 拦截器
  'hookConsole',    // 拦截器
  'simplelogger.ts',  // 当前文件
  'logger-boot.ts',  // 入口文件
  'at Object.log', // 原生 console 方法
];
//error堆栈：[
//  "Error",                                                    // [0] Error 对象本身
//  "    at processLogOutput (simplelogger.ts:539:25)",         // [1] processLogOutput 函数
//  "    at intercept (simplelogger.ts:651:15)",               // [2] intercept 拦截器
//  "    at Object.log (simplelogger.ts:656:20)",               // [3] logger.log 方法
//  "    at UserComponent.method (UserComponent.vue:45:12)",   // [4] 真正的用户代码 
//  "    at HTMLButtonElement.onclick (index.html:12:34)"      // [5] 事件处理器
//
//从堆栈中提取真正的调用位置
//跳过 logger 内部的函数，找到第一个来自代码或组件库的调用位置

function getCallerLocation(stack: string[]): string {
  // 从堆栈的第 3 行开始查找，[3] 开始才是可能的真实调用位置
  for (let i = 3; i < stack.length; i++) {
    const line = stack[i] || '';
    // 检查这一行是否包含 logger 内部函数
    const isLoggerInternal = loggerInternalPatterns.some(pattern =>
      line.includes(pattern)
    );
    //  找到第一个非 logger 内部的调用位置
    if (!isLoggerInternal && (line.includes(':') || line.includes('at '))) {
      return line.trim();
    }
  }
  // 如果找不到，返回第一个有效的堆栈行（跳过 Error 和 processLogOutput）
  return stack[2] || stack[1] || '';
  // 真正的用户代码通常在索引4（第5行），直接取索引4
  // 如果索引4不存在或包含 logger 内部函数，则尝试索引3
  // const line4 = stack[4] || '';

  // if (line4 && !loggerInternalPatterns.some(pattern => line4.includes(pattern))) {
  //   return line4.trim();
  // }
  // // 返回索引3或索引2
  // return (stack[3] || stack[2] || stack[1] || '').trim();
}
// 处理输出格式
function formatArgs(args: any[]): string {
  return args.map(arg => {
    if (typeof arg === 'object' && arg !== null) {
      try {
        return JSON.stringify(arg);
      } catch {
        return '[object circular]';
      }
    }
    return String(arg);
  }).join(' ');
}
async function processLogOutput(args: any[], level: 'log' | 'info' | 'warn' | 'error', skipFileWrite = false) {
  // 如果跳过文件写入（例如在 flush 期间）,跳过写入日志文件，等执行完再processLogOutput执行写入文件
  if (skipFileWrite) {
    return;
  }
  // 1. 原始输出
  originalConsole[level](
    ...args.map(a =>
      typeof a === 'object'
        ? (() => {
          try {
            return JSON.stringify(a, null, 2);
          } catch {
            return '[object circular]';
          }
        })()
        : String(a)
    )
  );
  // 格式化日志参数
  const formattedString = formatArgs(args);

  // 2. 获取堆栈信息（所有平台都需要）
  const error = new Error();
  const stack = (error.stack || '').split('\n');
  const callerLine = getCallerLocation(stack);
  // SourceMap 解析（异步），将压缩后位置映射回源码
  const mappedLine = await mapToSource(callerLine);

  // 3. 检查是否为 HarmonyOS 平台
  const isHarmonyOS = Capacitor.getPlatform() === 'harmony' ||
    (typeof (window as any).HarmonyBridge !== 'undefined');

  if (isHarmonyOS && (window as any).Capacitor?.Plugins?.Logger) {
    // HarmonyOS 平台：调用原生 Logger 插件，包含堆栈信息
    try {
      const LoggerPlugin = (window as any).Capacitor.Plugins.Logger;
      // 将消息和堆栈信息组合
      const messageWithStack = `${formattedString} →→→: ${mappedLine}`;
      await LoggerPlugin.log({
        level: level === 'log' ? 'info' : level, // 将 'log' 映射为 'info'
        tag: 'WebApp',
        message: messageWithStack
      });
    } catch (error) {
      // 如果原生日志失败，降级到内存日志
      originalConsole.warn('原生日志写入失败，降级到内存日志:', error);
      isLoggerInternal = true;
      try {
        logger[level](formattedString, `→→→: ${mappedLine}`);
      } finally {
        isLoggerInternal = false;
      }
    }
  } else {
    // 其他平台：使用内存日志系统
    isLoggerInternal = true;
    try {
      logger[level](formattedString, `→→→: ${mappedLine}`);
    } finally {
      isLoggerInternal = false;
    }
  }
}

//Logger 内部使用的 console 方法
//既输出到控制台又写入文件，但不通过 intercept 避免递归
//使用场景：
// - logger 内部的调试信息
// - 文件操作的状态输出
// - 系统初始化信息
const log = {
  log: (...args: any[]) => {
    isLoggerInternal = true;
    // 如果正在 flush，只输出到控制台，不写入文件，避免重复和递归
    const skipFileWrite = logger.isFlushing();
    processLogOutput(args, 'log', skipFileWrite);
    isLoggerInternal = false;
  },
  info: (...args: any[]) => {
    isLoggerInternal = true;
    const skipFileWrite = logger.isFlushing();
    processLogOutput(args, 'info', skipFileWrite);
    isLoggerInternal = false;
  },
  warn: (...args: any[]) => {
    isLoggerInternal = true;
    const skipFileWrite = logger.isFlushing();
    processLogOutput(args, 'warn', skipFileWrite);
    isLoggerInternal = false;
  },
  error: (...args: any[]) => {
    isLoggerInternal = true;
    const skipFileWrite = logger.isFlushing();
    processLogOutput(args, 'error', skipFileWrite);
    isLoggerInternal = false;
  }
};

//标记是否已经 hook 过 console
let isConsoleHooked = false;

// 拦截全局 console 方法，实现统一的日志处理
//功能
//1. 保持原生控制台输出
//2. 自动写入日志文件
//. 防止递归调用
// 过滤第三方库输出
// 调用时机：应用启动时最早执行（在 logger-boot.ts 中）
export function hookConsole() {
  // 防止重复 hook
  if (isConsoleHooked) {
    return;
  }
  isConsoleHooked = true;

  // 在 hook 之前保存原生 console 方法的引用（确保是真正的原生方法）
  if (!originalConsole) {
    originalConsole = {
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    };
  }
  // 若为 Web环境 ，则直接跳过 hook，保持原生 console 之前：生产环境默认不 hook：避免影响性能、第三方库输出等。
  // 开发环境在浏览器里用 SimpleLogViewer 查看日志。
  // 如需在 Web 生产环境也 hook，可通过环境变量 VITE_ENABLE_WEB_CONSOLE_HOOK=true 显式开启。
  try {
    const { Capacitor } = require('@capacitor/core');
    const enableWebConsoleHook = (import.meta as any).env?.VITE_ENABLE_WEB_CONSOLE_HOOK === 'true';
    if (Capacitor?.getPlatform?.() === 'web' && !enableWebConsoleHook && !(import.meta as any).env?.DEV) {
      // Web 平台默认不拦截 console：
      // 在 DevTools 里看到的 console.log 只是浏览器输出
      // 不会进入 logger 的 historyLogs，因此 LogViewer 可能显示“暂无日志记录”
      // 需在 Web 端也采集 console → logger，在环境变量中设置：VITE_ENABLE_WEB_CONSOLE_HOOK=true
      // originalConsole!.log('[Hook] Skip console hook on web');
      return;
    }
  } catch (_) { }
  // 自动初始化环境配置
  initInfoLoggingEnabled();

  // 创建拦截器函数
  // @param original - 原生 console 方法
  //@param level - 日志级别
  //@returns 拦截后的函数
  const intercept = (original: Function, level: 'log' | 'info' | 'warn' | 'error') => {
    return (...args: any[]) => {
      // 1. 先执行原生输出（保持控制台显示）
      // 所有平台都输出原始参数，保持调试体验一致
      original(...args);
      //   original(
      //   ...args.map(a =>
      //     typeof a === 'object'
      //       ? (() => {
      //           try {
      //             return JSON.stringify(a, null, 2);
      //           } catch {
      //             return '[object circular]';
      //           }
      //         })()
      //       : String(a)
      //   )
      // );
      // 2. 递归防护：如果正在处理 logger 内部的日志，跳过
      if (isLoggerInternal) return;

      // 3. 过滤第三方库输出，避免日志污染
      const stack = new Error().stack || '';
      if (stack.includes('node_modules/') || stack.includes('capacitor-')) return;

      // 4. 使用统一处理函数写入文件（不等待，避免阻塞）
      void processLogOutput(args, level);
    };
  };



  // 5. 替换全局 console 方法

  if (import.meta.env.VITE_USE_CONSOLE_HOOK === 'true') {
    // console.log = intercept(originalConsole.log, 'log');
    // console.info = intercept(originalConsole.info, 'info');
    // console.warn = intercept(originalConsole.warn, 'warn');
    // console.error = intercept(originalConsole.error, 'error');

  }



  // 6. 全局替换：确保所有环境都能使用拦截后的 console
  if (typeof window !== 'undefined') (window as any).console = console;
  if (typeof globalThis !== 'undefined') (globalThis as any).console = console;

  // 7. 输出初始化完成信息（使用原生 console 避免递归）
  originalConsole.log('[Hook] Console hook 已启用');
}
